import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { useCountdown } from "../hooks/useCountdown";
import { playAlarm } from "../lib/alarm";
import {
  type Clock,
  clockToSeconds,
  formatDuration,
  secondsToClock,
} from "../lib/time";
import { TimeDisplay } from "./TimeDisplay";
import { TimerControls } from "./TimerControls";

// 1, 5, 10, 15, 25, 30 minutes — quick-set buttons (all whole minutes).
const QUICK_PRESETS = [60, 300, 600, 900, 1500, 1800];

function ClockInput({
  id,
  label,
  value,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  max: number;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={id}
        className="block text-center text-xs text-muted-foreground"
      >
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        max={max}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="w-20 border-black/15 bg-white text-center text-lg text-neutral-900 dark:border-black/15 dark:bg-white dark:text-neutral-900"
      />
    </div>
  );
}

export function CountdownTimer({
  initialSeconds,
  editable = false,
}: {
  initialSeconds: number;
  editable?: boolean;
}) {
  const timer = useCountdown(initialSeconds, playAlarm);
  const idle =
    !timer.running && !timer.finished && timer.remaining === timer.duration;
  const clock = secondsToClock(timer.duration);

  const setField = (field: keyof Clock, raw: string) => {
    const n = Math.max(0, Math.floor(Number(raw) || 0));
    const capped = field === "hours" ? Math.min(n, 99) : Math.min(n, 59);
    timer.setDuration(clockToSeconds({ ...clock, [field]: capped }));
  };

  return (
    <div className="pct-card space-y-8 rounded-2xl p-6 sm:p-10">
      <TimeDisplay
        value={formatDuration(timer.remaining)}
        finished={timer.finished}
        label={m.timer_remaining_label()}
      />

      {timer.finished && (
        <p className="text-center font-semibold text-destructive">
          {m.timer_times_up()}
        </p>
      )}

      {editable && idle && (
        <div className="flex items-end justify-center gap-3">
          <ClockInput
            id="timer-hours"
            label={m.timer_hours()}
            value={clock.hours}
            max={99}
            onChange={(v) => setField("hours", v)}
          />
          <ClockInput
            id="timer-minutes"
            label={m.timer_minutes()}
            value={clock.minutes}
            max={59}
            onChange={(v) => setField("minutes", v)}
          />
          <ClockInput
            id="timer-seconds"
            label={m.timer_seconds()}
            value={clock.seconds}
            max={59}
            onChange={(v) => setField("seconds", v)}
          />
        </div>
      )}

      <TimerControls
        running={timer.running}
        onStart={timer.start}
        onPause={timer.pause}
        onReset={timer.reset}
      />

      {editable && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {QUICK_PRESETS.map((s) => (
            <Button
              key={s}
              size="sm"
              variant="secondary"
              onClick={() => timer.setDuration(s)}
            >
              {`${s / 60}m`}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
